<template lang="pug">
//- One desk's time in the header, as ONE control: the clock and the way to book
//- time the clock was not running for. Two of these sit side by side — ours and
//- the shop's — so each names its desk in both states; a pair of unlabelled
//- buttons said nothing about which was which.
.support-time(:class="[`support-time--${desk}`, { 'support-time--running': !!stash, 'support-time--paused': isPaused }]")
    //- Nothing running here: the way to start one, in the place the running
    //- clock would be. Without it a timer could only be started from a ticket,
    //- which means finding or inventing one before the phone call can begin.
    .support-time__main(
        v-if="!stash"
        :title="$t('Track time on {desk}', { desk: deskLabel })"
        @click="requestStart"
    )
        i.el-icon-fa-stopwatch.fas
        span.support-time__desk {{ deskLabel }}

    .support-time__main(v-else :title="$t('Open the running session')" @click="sessionDialog = true")
        i.support-time__dot
        span.support-time__ticket(:title="stash.subject") {{ stash.ticketNumber }}
        span.support-time__desk {{ deskLabel }}
        span.support-time__elapsed {{ elapsed }}

    //- An interruption is sudden, so the pause is one click from anywhere:
    //- the clock stands still (amber dot, frozen time) and the same button
    //- lets it count again - on the SAME entry, so a break is not billed as
    //- a second fragment rounded up on its own.
    button.support-time__pause(
        v-if="stash"
        type="button"
        :title="isPaused ? $t('Resume the clock') : $t('Pause the clock')"
        :disabled="pausing"
        @click="togglePause"
    )
        i.fas(:class="isPaused ? 'el-icon-fa-play' : 'el-icon-fa-pause'")

    //- Time that was already worked: the call taken before anyone reached for
    //- the timer. Beside the clock rather than inside the start dialog, because
    //- that dialog is out of reach exactly while a clock is running.
    button.support-time__log(
        type="button"
        :title="$t('Log time on {desk}', { desk: deskLabel })"
        @click="logDialog = true"
    )
        i.el-icon-fa-plus.fas

    //- Who is not in today, on our desk's pill only: a count, the names in
    //- the tooltip, the calendar one click away. Nothing when everyone is in.
    button.support-time__away(
        v-if="awayToday.length"
        type="button"
        :title="awayTitle"
        @click="$router.push({ name: 'system-team-absences' })"
    )
        i.el-icon-fa-umbrella-beach.fas
        span {{ awayToday.length }}

    //- Everything the call produces — subject, description, dates, files and
    //- the running note record — without leaving the call to find the ticket.
    running-session-drawer(
        v-if="stash"
        :visible.sync="sessionDialog"
        :stash="stash"
        @stop="stopSession"
    )

    add-time-entry-dialog(:visible.sync="logDialog" :desk="desk" :cms-id="cmsId")
</template>

<i18n>
{
    "en": {
        "Track time on {desk}": "Track time on {desk}",
        "Log time on {desk}": "Log time on {desk}",
        "until {date}": "until {date}"
    },
    "de": {
        "Not in today": "Heute nicht da",
        "sick": "krank",
        "on vacation": "Urlaub",
        "until {date}": "bis {date}",
        "Pause the clock": "Erfassung pausieren",
        "An error occurred!": "Ein Fehler ist aufgetreten!",
        "Resume the clock": "Erfassung fortsetzen",
        "The clock could not be paused.": "Die Erfassung konnte nicht pausiert werden.",
        "Platform": "Plattform",
        "Website": "Webauftritt",
        "Track time": "Zeit erfassen",
        "Track time on {desk}": "Zeit auf {desk} erfassen",
        "Log time on {desk}": "Zeit nachtragen auf {desk}",
        "Open the running session": "Laufende Erfassung öffnen",
        "Log time": "Zeit nachtragen"
    }
}
</i18n>

<script>
import _ from 'lodash';

import { useSetupStore, useTicketStore } from '@/stores';
import { activeSeconds, formatClock } from '@/helpers/duration';
import RunningSessionDrawer from '@/extensions/tickets/components/RunningSessionDrawer';
import AbsenceApi from '@/store/api/system/absence';
import { format, parseISO } from 'date-fns';
import AddTimeEntryDialog from '@/extensions/tickets/components/AddTimeEntryDialog';

export default {
    name: 'SupportTimeTracker',
    components: { RunningSessionDrawer, AddTimeEntryDialog },
    props: {
        // Which desk this pill is for. One instance per desk, so OUR support
        // session and a shop's own helpdesk timer are two pills that never
        // overwrite one another.
        desk: {
            type: String,
            required: true,
        },
        // The shop a tenant pill belongs to. It matters: the security layer
        // does not narrow a super user, so asking for the tenant desk without
        // it returns every shop's running entries.
        cmsId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            now: Date.now(),
            ticker: null,
            sessionDialog: false,
            pausing: false,
            logDialog: false,
            // Today's approved absences of the team (platform desk only).
            awayToday: [],
        };
    },
    computed: {
        awayTitle() {
            const lines = this.awayToday.map((absence) => {
                const name = `${_.get(absence, 'user.first_name') || ''} ${_.get(absence, 'user.last_name') || ''}`.trim();
                const kind = absence.type === 'sick' ? this.$t('sick') : this.$t('on vacation');
                const until = absence.starts_on === absence.ends_on ? '' : ' ' + this.$t('until {date}', { date: format(parseISO(absence.ends_on), 'dd.MM.') });

                return `${name}, ${kind}${until}`;
            });

            return `${this.$t('Not in today')}: ${lines.join(' · ')}`;
        },
        ticketStore() {
            return useTicketStore();
        },
        // ONE source of truth: the store. syncRunningSession writes the server's
        // answer into it and clears it when nothing is running — a local copy
        // here outlived the session it described (the pill survived leaving the
        // account, because clearing the stash could not reach it).
        stash() {
            return this.isPlatform ? this.ticketStore.platformTrackingStash : this.ticketStore.trackingStash;
        },
        isPlatform() {
            return this.desk === 'platform';
        },
        // Which of the two pills this is, in one word. Not the customer name on
        // both: our support ticket FOR a shop and that shop's own ticket then
        // read identically apart from the number, which is the one thing the
        // label exists to fix. The desk is what differs, so the desk is what it
        // says — which customer our session is for is on the ticket itself.
        deskLabel() {
            if (this.isPlatform) return this.$t('Platform');

            // Before anything runs there is no stash to read the name from, and
            // the start button needs to say which shop it would start on.
            return _.get(this.stash, 'cmsName') || _.get(useSetupStore(), 'activeCms.name') || this.$t('Website');
        },
        sessionTick() {
            return this.ticketStore.sessionTick;
        },
        // The desk moved somewhere else (another tab paused, resumed, started
        // or stopped a clock): the server broadcasts it, the store counts it.
        deskTick() {
            return this.ticketStore.deskTick;
        },
        isPaused() {
            return !!(this.stash && this.stash.pausedAt);
        },
        // The active time, not the wall time: a pause does not count.
        elapsed() {
            if (!this.stash || !this.stash.startedAt) return '00:00';

            return formatClock(activeSeconds(this.stash, this.now));
        },
    },
    mounted() {
        if (this.desk === 'platform') this.loadAwayToday();
        this.ticker = setInterval(() => {
            this.now = Date.now();
        }, 1000);

        this.syncRunningSession();
    },
    watch: {
        // Entering or leaving a customer's account does not remount this, and a
        // session started or stopped elsewhere is invisible until something asks.
        $route() {
            this.syncRunningSession();
        },
        // A timer started or stopped on a ticket page.
        sessionTick() {
            this.syncRunningSession();
        },
        deskTick() {
            this.syncRunningSession();
        },
    },
    beforeDestroy() {
        if (this.ticker) clearInterval(this.ticker);
    },
    methods: {
        loadAwayToday() {
            const today = format(new Date(), 'yyyy-MM-dd');

            AbsenceApi.fetchAbsences({ from: today, to: today, status: 'approved' })
                .then((absences) => {
                    this.awayToday = absences;
                })
                .catch(() => {
                    this.awayToday = [];
                });
        },
        // The store owns the resync (the start dialog asks for the same thing
        // on opening); the pill only says which desk and, for a shop, which.
        syncRunningSession() {
            this.ticketStore.syncRunningSession(this.desk, this.isPlatform ? null : this.cmsId);
        },
        // The shops this desk may open a ticket for. A shop's own desk has
        // exactly one — the one being looked at.
        requestStart() {
            const setup = useSetupStore();
            // Our own desk supports every customer, so the dialog searches for
            // one instead of being handed the handful this user administers.
            const cmsOptions = this.isPlatform
                ? []
                : [_.get(setup, 'activeCms')].filter((cms) => _.get(cms, 'id'));

            this.ticketStore.requestTimerStart({
                desk: this.desk,
                cmsId: this.isPlatform ? null : this.cmsId,
                cmsOptions,
            });
        },
        // The same sheet leaving an account opens, for this desk — and
        // cancellable, because nothing here forces the session to end.
        stopSession() {
            this.sessionDialog = false;
            this.ticketStore.openStopDialog(this.desk, { dismissible: true });
        },
        togglePause() {
            if (this.pausing) return;

            this.pausing = true;
            this.ticketStore
                .togglePause(this.desk)
                .catch((error) => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(error);
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: _.get(error, 'message') || this.$t('The clock could not be paused.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                    // Whatever the server says now is the truth.
                    this.syncRunningSession();
                })
                .finally(() => {
                    this.pausing = false;
                });
        },
    },
};
</script>

<style lang="sass" scoped>
// One rounded control per desk: the clock on the left, the way to book past
// time on the right, a hairline between them. Two separate floating buttons
// read as four unrelated controls.
.support-time
    display: flex
    align-items: stretch
    height: 32px
    border-radius: 16px
    border: 1px solid rgba(255, 255, 255, .25)
    color: rgba(255, 255, 255, .75)
    font-size: .78rem
    white-space: nowrap
    overflow: hidden

    // A clock that is running is the loud state: filled, not outlined.
    &--running
        border-color: transparent
        background: rgba(255, 255, 255, .12)
        color: #fff

.support-time__main
    display: flex
    align-items: center
    gap: 6px
    padding: 0 10px
    cursor: pointer

    &:hover
        background: rgba(255, 255, 255, .12)
        color: #fff

.support-time__away
    display: flex
    align-items: center
    gap: 4px
    padding: 0 8px
    border: 0
    border-left: 1px solid rgba(255, 255, 255, .25)
    background: none
    color: inherit
    font-size: .72rem
    cursor: pointer

.support-time__log
    display: flex
    align-items: center
    justify-content: center
    width: 30px
    padding: 0
    border: 0
    border-left: 1px solid rgba(255, 255, 255, .25)
    background: none
    color: inherit
    font-size: .72rem
    cursor: pointer

    &:hover
        background: rgba(255, 255, 255, .12)
        color: #fff

.support-time__dot
    width: 8px
    height: 8px
    border-radius: 50%
    background: #67c23a
    // A running session, not a static badge.
    animation: support-time-pulse 2s ease-in-out infinite

// Standing still: the dot goes amber and stops pulsing, the time freezes.
.support-time--paused
    .support-time__dot
        background: #E6A23C
        animation: none

    .support-time__elapsed
        opacity: .6

.support-time__pause
    display: flex
    align-items: center
    justify-content: center
    width: 30px
    padding: 0
    border: 0
    border-left: 1px solid rgba(255, 255, 255, .25)
    background: none
    color: inherit
    font-size: .68rem
    cursor: pointer

    &:hover
        background: rgba(255, 255, 255, .12)
        color: #fff

    &:disabled
        opacity: .5
        cursor: default

.support-time__ticket
    font-weight: 600

// Which desk this is. Quiet next to the number and the clock, but always there:
// it is the only thing telling two of these apart. Narrow screens keep the
// clock and drop the words around it rather than dropping the control.
.support-time__desk
    display: none
    opacity: .7
    max-width: 12ch
    overflow: hidden
    text-overflow: ellipsis
    +res(lg)
        display: inline

.support-time__elapsed
    font-variant-numeric: tabular-nums
    opacity: .85

@keyframes support-time-pulse
    0%, 100%
        opacity: 1
    50%
        opacity: .35
</style>
